<template>
  <div class="credential-settings d-flex align-items-center">
    <div v-if="showBalance" class="credential-settings__balance">
      <router-link
        v-if="!isPublisher && $can('visit', 'Payments')"
        to="/payments"
        class="balance-link text-decoration-none"
      >
        <template v-if="!isBalanceLoading">
          <svg-icon icon="hp-dollar" class="icon-balance" />
          <span class="align-center" :class="{ 'text-danger': isNegativeBalance }">
            {{ formattedBalance }}
          </span>
        </template>
        <round-loader v-else size="16" />
      </router-link>
      <router-link
        v-if="isPublisher"
        to="/payments"
        v-b-tooltip.hover.bottom="'Unpaid earnings'"
        class="balance-link"
      >
        <template v-if="!isBalanceLoading">
          <svg-icon icon="hp-dollar" class="icon-balance" />
          <span>
            {{ formattedPublisherBalance }}
          </span>
        </template>
        <round-loader v-else size="16" />
      </router-link>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';

import RoundLoader from '@/components/UI/RoundLoader.vue';

export default {
  name: 'TheCredentialSettings',
  components: { RoundLoader },

  props: {
    showBalance: {
      type: Boolean,
      default: true,
    },
  },

  computed: {
    ...mapGetters({
      user: 'user',
    }),
    ...mapState({
      userBalance: (state) => state.app.userBalance,
      publisherUnpaidBalance: (state) => state.app.publisherUnpaidBalance,
      isBalanceLoading: (state) => state.app.isBalanceLoading,
    }),
    isPublisher() {
      return this.$can('show', 'unpaidBalance');
    },
    isNegativeBalance() {
      return this.userBalance.amount < 0;
    },
    formattedBalance() {
      const amount = this.userBalance.amount || 0;
      // Format to 2 decimal places
      const formattedAmount = amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return formattedAmount + ' ' + (this.userBalance.currency || '');
    },
    formattedPublisherBalance() {
      const amount = this.publisherUnpaidBalance.amount || 0;
      // Format to 2 decimal places
      const formattedAmount = amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return formattedAmount + ' ' + (this.publisherUnpaidBalance.currency || '');
    },
  },

  methods: {
    async getBalance() {
      if (!this.showBalance) {
        return;
      }
      if (!['advertiser', 'publisher'].includes(this.$store.getters.role)) {
        return;
      }
      if (this.$store.getters.role === 'publisher') {
        this.$store.dispatch('fetchPublisherUnpaidBalance');
      } else {
        this.$store.dispatch('fetchUserBalance', this.user.id);
      }
    },
  },

  created() {
    if (this.showBalance) {
      if (this.isPublisher) {
        this.$store.dispatch('fetchPublisherUnpaidBalance');
      } else if (this.$can('show', 'Balance')) {
        this.$store.dispatch('fetchUserBalance', this.user.id);
      }
    }
  },

  watch: {
    'user.id': {
      handler(val) {
        if (!val || !this.showBalance) {
          return;
        }
        if (this.isPublisher) {
          this.$store.dispatch('fetchPublisherUnpaidBalance');
        } else if (this.$can('show', 'Balance')) {
          this.$store.dispatch('fetchUserBalance', this.user.id);
        }
      },
    },
    async role() {
      await this.getBalance();
    },
    async showBalance() {
      await this.getBalance();
    },
  },
};
</script>

<style lang="scss" scoped>
.credential-settings {
  height: 100%;
  margin: 0 0.75rem;
}
.credential-settings__balance {
  display: flex;
  align-items: center;
}

.balance-link {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--bs-body-color);
  font-size: 0.75rem;
  font-weight: bold;
}

:deep(.icon-balance) {
  svg {
    color: var(--bs-warning);
    width: 16px !important;
    height: 16px !important;
    margin: 0;
  }
}
</style>
